import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import Points from '@wix/da-ds/pkg/Icons/16x16/Points';
import Tag from '@wix/da-ds/pkg/TagList/Tag';
import Surface from '@wix/da-ds/pkg/Surface';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import { STUDIO_EARNINGS_TRANSACTIONS_STREAM } from '../../../../constants';
import StudioPagination from '../../StudioPagination/StudioPagination';
import { fetchTransactions } from '../../../actions/studio/earnings';

import s from './TransactionsTable.scss';

export interface Props {
  className?: string;
}

const TransactionsTable: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const {
    items: transactions,
    hasMore,
    itemsPerFetch,
  } = useStream(STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamId);

  const fromOffset = transactions.length > 0 ? 1 : 0;
  const toOffset = transactions.length;
  const hasLess = currentPage > 1;

  useEffect(() => {
    setIsLoading(false);
  }, [transactions]);

  const handleFetchNext = (streamId: string) => {
    const limit =
      itemsPerFetch ||
      STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamConfig.itemsPerFetch;
    const offset = currentPage * limit;
    setIsLoading(true);
    dispatch(fetchTransactions(streamId, limit, offset));
    setCurrentPage(prev => prev + 1);
  };

  const handleFetchPrevious = (streamId: string) => {
    const limit =
      itemsPerFetch ||
      STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamConfig.itemsPerFetch;
    const offset = Math.max(0, (currentPage - 2) * limit);
    setIsLoading(true);
    dispatch(fetchTransactions(streamId, limit, offset));
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  if (transactions.length === 0 && !isLoading) {
    return null;
  }

  if (isLoading && transactions.length === 0) {
    return (
      <div className={cn(s['root'], className)}>
        <div className={s['loading-container']}>
          <LoadingIndicator view="llama" />
        </div>
      </div>
    );
  }

  const formatTransactionDate = (date: string | null) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isVoidedOrRefunded = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    return normalizedStatus === 'voided' || normalizedStatus === 'refunded';
  };

  return (
    <Surface
      type={ThemeSurface.SECONDARY}
      className={cn(s['root'], className)}
      themeOverrideOnly
    >
      <div className={s['table-container']}>
        {isLoading && (
          <div className={s['loading-overlay']}>
            <LoadingIndicator view="llama" />
          </div>
        )}
        <table className={s['table']}>
          <thead className={s['table-header']}>
            <tr className={cn(s['table-row'], s['pagination-row'])}>
              <th className={s['header-cell']}></th>
              <th className={s['header-cell']}></th>
              <th className={s['header-cell']}></th>
              <th className={cn(s['header-cell'], s['pagination-cell'])}>
                <StudioPagination
                  streamId={STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamId}
                  hasLess={hasLess}
                  hasMore={hasMore}
                  fromOffset={fromOffset}
                  toOffset={toOffset}
                  fetchPrevious={handleFetchPrevious}
                  fetchNext={handleFetchNext}
                  showButtons={true}
                  showResultsCount={false}
                  adaptCounterToThumbSize={false}
                  showFirstLastPageButtons={false}
                />
              </th>
            </tr>
            <tr>
              <th className={s['header-cell']}>
                {t('studio.earnings.transactions.headers.details')}
              </th>
              <th className={s['header-cell']}>
                {t('studio.earnings.transactions.headers.product')}
              </th>
              <th className={s['header-cell']}>
                {t('studio.earnings.transactions.headers.status')}
              </th>
              <th className={s['header-cell']}>
                {t('studio.earnings.transactions.headers.earnings')}
              </th>
            </tr>
          </thead>
          <tbody className={s['table-body']}>
            {transactions.map(transaction => (
              <tr key={transaction.transactionId} className={s['table-row']}>
                <td className={s['cell']}>
                  <div className={s['details']}>
                    <div
                      className={cn(
                        s['transaction-type'],
                        s[
                          `transaction-type--${transaction.transactionEntryType}`
                        ]
                      )}
                    >
                      {transaction.transactionEntryType === 'credit'
                        ? t('studio.earnings.transactions.credit')
                        : t('studio.earnings.transactions.debit')}
                    </div>
                    <div className={s['transaction-date']}>
                      {formatTransactionDate(transaction.transactionDate)}
                    </div>
                  </div>
                </td>
                <td className={s['cell']}>
                  <div className={s['product']}>
                    <div className={s['product-name']}>
                      {transaction.productUrl ? (
                        <a
                          href={transaction.productUrl}
                          className={s['product-link']}
                        >
                          {transaction.productName}
                        </a>
                      ) : (
                        transaction.productName
                      )}
                    </div>
                    {transaction.productType && (
                      <div className={s['product-type']}>
                        {transaction.productType}
                      </div>
                    )}
                  </div>
                </td>
                <td className={s['cell']}>
                  <Tag size="small" className={s['status-tag']}>
                    {transaction.status}
                  </Tag>
                </td>
                <td className={s['cell']}>
                  <div className={s['earnings']}>
                    <div
                      className={cn(
                        s['earnings-amount'],
                        isVoidedOrRefunded(transaction.status) && s['voided']
                      )}
                    >
                      {formatDollars(transaction.earnings.usd)}
                      &nbsp;
                      {transaction.earnings.points !== 0 && (
                        <span
                          className={cn(
                            s['earnings-points'],
                            isVoidedOrRefunded(transaction.status) &&
                              s['voided']
                          )}
                        >
                          (
                          <Points className={s['points-icon']} />
                          {Math.abs(transaction.earnings.points)})
                        </span>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            <tr className={cn(s['table-row'], s['pagination-row'])}>
              <td className={s['cell']}></td>
              <td className={s['cell']}></td>
              <td className={s['cell']}></td>
              <td className={cn(s['cell'], s['pagination-cell'])}>
                <StudioPagination
                  streamId={STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamId}
                  hasLess={hasLess}
                  hasMore={hasMore}
                  fromOffset={fromOffset}
                  toOffset={toOffset}
                  fetchPrevious={handleFetchPrevious}
                  fetchNext={handleFetchNext}
                  showButtons={true}
                  showResultsCount={false}
                  adaptCounterToThumbSize={false}
                  showFirstLastPageButtons={false}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={s['mobile-container']}>
        {isLoading && (
          <div className={s['loading-overlay']}>
            <LoadingIndicator view="llama" />
          </div>
        )}
        <div className={s['mobile-transactions']}>
          {transactions.map(transaction => (
            <div key={transaction.transactionId} className={s['mobile-card']}>
              <div className={s['mobile-row']}>
                <div
                  className={cn(
                    s['mobile-label'],
                    s['mobile-transaction-type'],
                    s[transaction.transactionEntryType]
                  )}
                >
                  {transaction.transactionEntryType === 'credit'
                    ? t('studio.earnings.transactions.credit')
                    : t('studio.earnings.transactions.debit')}
                </div>
                <div className={s['mobile-value']}>
                  <div
                    className={cn(
                      s['mobile-earnings-amount'],
                      isVoidedOrRefunded(transaction.status) && s['voided']
                    )}
                  >
                    {formatDollars(transaction.earnings.usd)}
                    {transaction.earnings.points !== 0 && (
                      <span
                        className={cn(
                          s['mobile-earnings-points'],
                          isVoidedOrRefunded(transaction.status) && s['voided']
                        )}
                      >
                        ({transaction.earnings.points < 0 ? '-' : ''}
                        <Points className={s['mobile-points-icon']} />
                        {Math.abs(transaction.earnings.points)})
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className={s['mobile-row']}>
                <div className={s['mobile-label']}>
                  {t('studio.earnings.transactions.headers.product')}
                </div>
                <div className={s['mobile-value']}>
                  <div className={s['mobile-product-container']}>
                    {transaction.productName && (
                      <div className={s['mobile-product-name']}>
                        {transaction.productUrl ? (
                          <a
                            href={transaction.productUrl}
                            className={s['mobile-product-link']}
                          >
                            {transaction.productName}
                          </a>
                        ) : (
                          transaction.productName
                        )}
                      </div>
                    )}
                    {transaction.productType && (
                      <div className={s['mobile-product-type']}>
                        {transaction.productType}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={s['mobile-row']}>
                <div className={s['mobile-label']}>
                  {t('studio.earnings.transactions.headers.date')}
                </div>
                <div className={s['mobile-value']}>
                  <div className={s['mobile-date']}>
                    {formatTransactionDate(transaction.transactionDate)}
                  </div>
                </div>
              </div>
              <div className={s['mobile-row']}>
                <div className={s['mobile-label']}>
                  {t('studio.earnings.transactions.headers.status')}
                </div>
                <div className={s['mobile-value']}>
                  <Tag size="small" className={s['mobile-status-tag']}>
                    {transaction.status}
                  </Tag>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={s['mobile-pagination-bottom']}>
          <StudioPagination
            streamId={STUDIO_EARNINGS_TRANSACTIONS_STREAM.streamId}
            hasLess={hasLess}
            hasMore={hasMore}
            fromOffset={fromOffset}
            toOffset={toOffset}
            fetchPrevious={handleFetchPrevious}
            fetchNext={handleFetchNext}
            showButtons={true}
            showResultsCount={false}
            adaptCounterToThumbSize={false}
            showFirstLastPageButtons={false}
          />
        </div>
      </div>
    </Surface>
  );
};

TransactionsTable.displayName = 'TransactionsTable';

export default TransactionsTable;
